import {passageInsertSlot,passageReorderTarget} from './storyPointerReorder.js';

// A single DOM-only visual projection shared by Paper Edit and Refine.
// Persisted Assembly order NEVER changes until pointer release. Instead the
// original row reserves its space, neighbours glide aside, and a lifted copy
// follows the pointer. This avoids jitter from React changing hit-test boxes
// while the user is still dragging.
export function createFloatingPassageDrag(container,card,from,clientX,clientY){
 if(!container||!card)return null;
 const cards=[...container.querySelectorAll('[data-passage-id]')];
 if(cards[from]!==card)return null;
 const bounds=card.getBoundingClientRect();
 const gap=parseFloat(getComputedStyle(container).rowGap||getComputedStyle(container).gap)||0;
 const draggedHeight=bounds.height+gap;
 const xOffset=clientX-bounds.left,yOffset=clientY-bounds.top;
 const root=document.createElement('div');
 root.className='passage-sort-portal screen-editor';
 root.setAttribute('aria-hidden','true');
 const story=document.createElement('div');
 story.className='paper-story is-compact-story paper-workspace';
 const list=document.createElement('div');list.className='paper-blocks';
 const ghost=card.cloneNode(true);
 ghost.removeAttribute('id');
 ghost.querySelectorAll('[id]').forEach(node=>node.removeAttribute('id'));
 ghost.querySelectorAll('button,input,textarea,select,a,[tabindex]').forEach(node=>{
  node.tabIndex=-1;node.setAttribute('aria-hidden','true');
 });
 ghost.classList.remove('selected','playing','dragging','drop-before');
 ghost.classList.add('passage-floating-card');
 // The floating copy is portalled outside the Assembly panel, so inheriting
 // --paper-sheet alone loses each interview's tint (and can turn white).
 // Snapshot the source card's *actual* computed surface and label border.
 const appearance=getComputedStyle(card);
 ghost.style.background=appearance.background;
 ghost.style.borderColor=appearance.borderColor;
 ghost.style.borderLeftColor=appearance.borderLeftColor;
 ghost.style.color=appearance.color;
 ghost.style.width=bounds.width+'px';
 ghost.style.left=bounds.left+'px';
 ghost.style.top=bounds.top+'px';
 list.appendChild(ghost);story.appendChild(list);root.appendChild(story);
 document.body.appendChild(root);
 container.classList.add('is-live-sorting');
 const shifted=new Map();
 const items=[...container.querySelectorAll('[data-passage-id],.story-section-divider')];
 const initialTransforms=new Map(items.map(element=>[element,element.style.transform]));
 const originalScroll=container.scrollTop;
 let lastClientX=clientX,lastClientY=clientY;
 let destination=from,slot=from,ended=false;
 const setShift=(element,amount)=>{
  const previous=shifted.get(element)||0;
  if(previous===amount)return;
  shifted.set(element,amount);
  element.style.transform=amount?'translate3d(0,'+amount+'px,0)':'';
 };
 // Account for our animated transforms when measuring hitboxes, not the
 // current transformed visual positions. Those would oscillate back and forth.
 const originalRects=()=>{
  return cards.map(el=>{
   const r=el.getBoundingClientRect(),matrix=getComputedStyle(el).transform;
   // Read the *rendered* in-flight transform; subtracting a target value
   // during its transition would make a passage oscillate between slots.
   const delta=matrix==='none'?0:new DOMMatrixReadOnly(matrix).m42;
   return {top:r.top-delta,bottom:r.bottom-delta};
  });
 };
 const update=(x,y)=>{
  if(ended)return {slot,destination};
  // React updates the list className when the drag state changes. Restore the
  // transient CSS mode after those renders, without touching the saved order.
  container.classList.add('is-live-sorting');
  lastClientX=x;lastClientY=y;
  ghost.style.left=(x-xOffset)+'px';
  ghost.style.top=(y-yOffset)+'px';
  const rects=originalRects();
  slot=passageInsertSlot(rects,y);
  destination=passageReorderTarget(from,slot,cards.length);
  // Source row keeps occupying its actual layout slot, but fades underneath
  // moving neighbours; the resulting gap opens at the prospective destination.
  for(let i=0;i<cards.length;i++){
   const shift=i!==from&&destination>from&&i<=destination&&i>from ? -draggedHeight
    : i!==from&&destination<from&&i>=destination&&i<from ? draggedHeight : 0;
   setShift(cards[i],shift);
  }
  const top=Math.min(rects[from].top,rects[destination]?.top??rects[from].top);
  const bottom=Math.max(rects[from].bottom,rects[destination]?.bottom??rects[from].bottom);
  for(const element of items){
   if(!element.classList.contains('story-section-divider'))continue;
   const r=element.getBoundingClientRect(),matrix=getComputedStyle(element).transform;
   const prev=matrix==='none'?0:new DOMMatrixReadOnly(matrix).m42;
   const mid=(r.top+r.bottom)/2-prev;
   const isBetween=mid>top&&mid<bottom;
   setShift(element,isBetween?(destination>from?-draggedHeight:destination<from?draggedHeight:0):0);
  }
  return {slot,destination};
 };
 // Hold the floating copy through the React commit. In particular an upward
 // reorder changes which *real* card occupies the gap; revealing that card
 // before React commits causes a pale flash as its CSS state switches.
 let settleFrame=null,settleFallback=null,settleCleanup=null;
 const finish=(cancel=false)=>{
  if(ended)return {slot,destination};
  ended=true;
  const moving=!cancel&&destination!==from;
  const styles=items.map(element=>({
   element,transition:element.style.transition,transform:initialTransforms.get(element)||''
  }));
  // Turning off transitions before clearing neighbour transforms prevents a
  // second transform/backdrop animation on pointerup.
  for(const {element} of styles)element.style.transition='none';
  if(moving)card.style.visibility='hidden';
  for(const {element} of styles)element.style.transform='';
  shifted.clear();
  container.classList.remove('is-live-sorting');
  const cleanup=()=>{
   if(settleFrame!==null)cancelAnimationFrame(settleFrame);
   if(settleFallback!==null)clearTimeout(settleFallback);
   settleFrame=null;settleFallback=null;settleCleanup=null;
   // CSS has already resolved to the new, selected source-colour card while
   // transition was disabled. Restore regular hover/reorder transitions only
   // once the card is visible in its final position.
   for(const {element,transition,transform} of styles){
    element.style.transform=transform;
    element.style.transition=transition;
   }
   card.style.visibility='';
   root.remove();
  };
  settleCleanup=cleanup;
  if(moving){
   // The next paint follows React's synchronous reordered DOM commit.
   // Do not animate the ghost or the card: a clean, single drop is the goal.
   settleFrame=requestAnimationFrame(cleanup);
   // Safety for a suspended/background tab (rAF can be throttled).
   settleFallback=setTimeout(cleanup,300);
  }else cleanup();
  return {slot,destination};
 };
 const destroy=()=>{
  if(settleCleanup){settleCleanup();return;}
  if(!ended)finish(true);
  root.remove();
 };
 update(clientX,clientY);
 return {update,finish,destroy,get destination(){return destination;},get initialScroll(){return originalScroll;},
  get lastPointer(){return {x:lastClientX,y:lastClientY};}};
}
